import { useQuery } from '@tanstack/react-query';

import type { ApiSuccess, PolicyEndorsementStatus } from '@alamo/types';

import { apiClient } from '../client';
import { queryKeys } from '../query-keys';

type QueueCountPayload = { count: number };
type QueueScope = 'admin' | 'mine';

const OPEN_STATUSES = [
  'awaiting_analysis',
  'awaiting_signature',
  'awaiting_inspection',
  'ready_to_apply',
  'applying',
  'scheduled',
  'needs_attention',
] as const satisfies readonly PolicyEndorsementStatus[];

/**
 * O contador é um número só, então vai numa requisição: `statuses` aceita a
 * lista inteira e o backend soma com `IN (...)`. Uma consulta por status
 * multiplicava por sete a carga de um badge que fica visível o tempo todo.
 */
function usePolicyEndorsementQueueCount(
  scope: QueueScope,
  endpoint: string,
  options?: { enabled?: boolean },
) {
  const enabled = options?.enabled ?? true;
  const params = { statuses: OPEN_STATUSES.join(','), page: 1, limit: 1 };
  const query = useQuery({
    queryKey:
      scope === 'admin'
        ? queryKeys.policyEndorsements.adminList(params)
        : queryKeys.policyEndorsements.mineList(params),
    queryFn: async () => {
      const response = await apiClient.get<ApiSuccess<QueueCountPayload>>(endpoint, { params });
      return response.data.data.count;
    },
    enabled,
    staleTime: 15_000,
    refetchInterval: 60_000,
  });

  return {
    data: query.data,
    isError: query.isError,
    isFetching: query.isFetching,
  };
}

export function useEndorsementQueueCount(options?: { enabled?: boolean }) {
  return usePolicyEndorsementQueueCount('admin', '/admin/policy-endorsements', options);
}

export function useMyEndorsementQueueCount(options?: { enabled?: boolean }) {
  return usePolicyEndorsementQueueCount('mine', '/policy-endorsements/mine', options);
}
